/**
 * Healthcheck document schemas for client SDK.
 *
 * Contains SDK-specific schemas that transform timestamps to Timestamp instances,
 * plus typed document refs and callable function helpers.
 */
import type {
  DocumentReference,
  CollectionReference,
  Firestore,
} from 'firebase/firestore';
import type {Functions, HttpsCallableResult} from 'firebase/functions';
import {z} from 'zod';
import {doc, collection, getFirestore} from 'firebase/firestore';
import {httpsCallable, getFunctions} from 'firebase/functions';

import {
  HealthcheckRequestSchema,
  getHealthcheckCollectionPath,
  getHealthcheckDocumentPath,
  QUERY_HEALTHCHECK_LOGS_FUNCTION,
  QueryHealthcheckLogRequestSchema,
} from '../documents/healthcheck';
import {ClientTimestampSchema, createConverter} from './sdk';

// ============================================================================
// SDK-Specific Schemas (with Timestamp transform)
// ============================================================================

/**
 * Healthcheck log schema with client Timestamp.
 * Timestamps are transformed to SDK Timestamp instances with toDate(), toMillis().
 */
export const HealthcheckLogSchema = HealthcheckRequestSchema.extend({
  timestamp: ClientTimestampSchema,
}).loose();

export type HealthcheckLog = z.infer<typeof HealthcheckLogSchema>;

/**
 * Query response schema with client Timestamp.
 */
export const QueryHealthcheckLogResponseSchema = z.object({
  logs: z.array(HealthcheckLogSchema).nullable(),
});

export type QueryHealthcheckLogResponse = z.infer<
  typeof QueryHealthcheckLogResponseSchema
>;

// ============================================================================
// Document Refs
// ============================================================================

const healthcheckConverter = createConverter({
  schema: HealthcheckLogSchema,
  options: {validateOnRead: true, validateOnWrite: false},
});

export function getHealthcheckDocRef({
  logId,
  firestore = getFirestore(),
}: {
  logId: string;
  firestore?: Firestore;
}): DocumentReference<HealthcheckLog> {
  return doc(firestore, getHealthcheckDocumentPath({logId})).withConverter(
    healthcheckConverter,
  );
}

export function getHealthcheckCollectionRef({
  firestore = getFirestore(),
}: {
  firestore?: Firestore;
} = {}): CollectionReference<HealthcheckLog> {
  return collection(firestore, getHealthcheckCollectionPath()).withConverter(
    healthcheckConverter,
  );
}

// ============================================================================
// Callable Function Helpers
// ============================================================================

/**
 * Creates a typed callable function wrapper.
 *
 * When `responseSchema` is provided, the response data is validated with
 * `schema.parse()` before being returned. This catches malformed responses
 * from Cloud Functions at the call site rather than downstream.
 */
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
export function createCallable<TRequest, TResponse>({
  functions = getFunctions(),
  name,
  responseSchema,
}: {
  functions?: Functions;
  name: string;
  responseSchema?: z.ZodType<TResponse>;
}): (data: TRequest) => Promise<HttpsCallableResult<TResponse>> {
  const callable = httpsCallable<TRequest, TResponse>(functions, name);
  if (!responseSchema) {
    return callable;
  }
  return async (data: TRequest) => {
    const result = await callable(data);
    return {...result, data: responseSchema.parse(result.data)};
  };
}

/**
 * Creates a callable function for querying healthcheck logs.
 */
export function createQueryHealthcheckLogs({
  functions = getFunctions(),
}: {
  functions?: Functions;
} = {}) {
  return createCallable<
    z.infer<typeof QueryHealthcheckLogRequestSchema>,
    QueryHealthcheckLogResponse
  >({
    functions,
    name: QUERY_HEALTHCHECK_LOGS_FUNCTION,
    responseSchema: QueryHealthcheckLogResponseSchema,
  });
}
